import { useEffect, useState, useRef } from 'react';
import { CalendarClock, History, Eye, Play, Zap, X, Activity } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { fmtDate } from '../../utils/formatUtils.js';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function fmtDateTime(str) {
  if (!str) return '—';
  try { return format(parseISO(str), 'd MMM yyyy HH:mm', { locale: nl }); }
  catch { return str; }
}

function nextRunFromCronExpr(schedule) {
  if (!schedule) return null;
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const minute = parseInt(parts[0], 10);
  const hour   = parseInt(parts[1], 10);
  if (isNaN(minute) || isNaN(hour) || parts[2] !== '*') return null;
  const now  = new Date();
  const next = new Date();
  next.setUTCHours(hour, minute, 0, 0);
  if (next <= now) next.setUTCDate(next.getUTCDate() + 1);
  return next;
}

function cronToHuman(schedule) {
  if (!schedule) return schedule;
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return schedule;
  const minute = parseInt(parts[0], 10);
  const hour   = parseInt(parts[1], 10);
  if (parts[2] === '*' && parts[3] === '*' && parts[4] === '*') {
    return `${t('cronDashboard.dagelijksOm')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} UTC`;
  }
  return schedule;
}

// Supabase Edge Function URL mapping per job
const JOB_FUNCTION = {
  'daily-invoice-reminders':         'send-invoice-reminders',
  'send-prospect-reminders-daily':   'send-prospect-reminders',
  'send-agreement-reminders':        'send-agreement-reminders',
  'process-membership-renewals':     'process-membership-renewals',
  'process-membership-reminders':    'process-membership-reminders',
  'process-expired-enrollments':     'process-expired-enrollments',
};

const JOB_LABELS = {
  'daily-invoice-reminders':         t('cronDashboard.jobFactuur'),
  'send-prospect-reminders-daily':   t('cronDashboard.jobProspect'),
  'send-agreement-reminders':        t('cronDashboard.jobAkkoord'),
  'process-membership-renewals':     t('cronDashboard.jobAbonnement'),
  'process-membership-reminders':    t('cronDashboard.jobVerloopHerinneringen'),
  'process-expired-enrollments':     t('cronDashboard.jobVerlopenUitnodigingen'),
};

const JOB_DESC = {
  'daily-invoice-reminders':         t('cronDashboard.jobFactuurDesc'),
  'send-prospect-reminders-daily':   t('cronDashboard.jobProspectDesc'),
  'send-agreement-reminders':        t('cronDashboard.jobAkkoordDesc'),
  'process-membership-renewals':     t('cronDashboard.jobAbonnementDesc'),
  'process-membership-reminders':    t('cronDashboard.jobVerloopHerinneringenDesc'),
  'process-expired-enrollments':     t('cronDashboard.jobVerlopenUitnodigingenDesc'),
};

// Preview RPC per job (voor live-bevestiging: haal actuele telling op)
const JOB_PREVIEW_RPC = {
  'daily-invoice-reminders':       'preview_invoice_reminders',
  'send-prospect-reminders-daily': 'preview_prospect_reminders',
  'send-agreement-reminders':      'preview_agreement_reminders',
};

const STATUS_COLORS = {
  succeeded: '#16a34a',
  failed:    '#dc2626',
  starting:  '#d97706',
  running:   '#2563eb',
};

// cron_run_log labels per Edge-Function-naam (job_name in cron_run_log).
const RUNLOG_LABELS = {
  'process-membership-renewals':  t('cronDashboard.jobAbonnement'),
  'process-membership-reminders': t('cronDashboard.jobVerloopHerinneringen'),
  'process-expired-enrollments':  t('cronDashboard.jobVerlopenUitnodigingen'),
  'send-invoice-reminders':       t('cronDashboard.jobFactuur'),
  'send-agreement-reminders':     t('cronDashboard.jobAkkoord'),
  'send-prospect-reminders':      t('cronDashboard.jobProspect'),
  'sync-external-calendar':       t('cronDashboard.jobSync'),
  'cron-health-check':            t('cronDashboard.jobHealthCheck'),
};

// De 8 jobs die we in de runstatus-tabel altijd tonen (ook als er nog
// geen run is): de 7 cron-EF's + de signaleringscheck.
const RUNLOG_JOBS = Object.keys(RUNLOG_LABELS);

// Kleur + label per cron_run_status.
const RUN_STATUS = {
  success: { color: '#16a34a', label: t('cronDashboard.statusSuccess') },
  partial: { color: '#d97706', label: t('cronDashboard.statusPartial') },
  failed:  { color: '#dc2626', label: t('cronDashboard.statusFailed') },
};

// Compacte, leesbare weergave van een jsonb summary-object.
function fmtSummary(summary) {
  if (!summary || typeof summary !== 'object') return '—';
  const entries = Object.entries(summary).filter(([, v]) =>
    typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean'
  );
  if (entries.length === 0) return '—';
  return entries.map(([k, v]) => `${k}: ${v}`).join(' · ');
}

const TYPE_LABELS = {
  reminder:           t('cronDashboard.typeHerinnering'),
  final_notice:       t('cronDashboard.typeAanmaning'),
  prospect_reminder:  t('cronDashboard.typeProspect'),
  agreement_reminder: t('cronDashboard.typeAkkoord'),
};

// ─────────────────────────────────────────────────────────────────
// UI helpers
// ─────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: '0 0 16px' }}>{title}</h2>
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '20px 24px',
      marginBottom: 16,
      ...style,
    }}>
      {children}
    </div>
  );
}

function Badge({ color, label }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 9999,
      fontSize: 12,
      fontWeight: 600,
      background: color + '1a',
      color,
      border: `1px solid ${color}44`,
    }}>{label}</span>
  );
}

function TableWrapper({ children }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        {children}
      </table>
    </div>
  );
}

const TH = ({ children }) => (
  <th style={{
    textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e5e7eb',
    color: '#6b7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
  }}>{children}</th>
);

const TD = ({ children, style }) => (
  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle', ...style }}>
    {children}
  </td>
);

// ─────────────────────────────────────────────────────────────────
// Live-bevestigings-modal
// ─────────────────────────────────────────────────────────────────
function LiveConfirmModal({ jobName, onConfirm, onCancel }) {
  const [confirmText, setConfirmText] = useState('');
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    // Haal actuele telling op via preview RPC
    const rpc = JOB_PREVIEW_RPC[jobName];
    if (!rpc) { setLoadingCount(false); return; }
    supabase.rpc(rpc).then(({ data, error }) => {
      if (!error) setPreviewCount((data || []).length);
      setLoadingCount(false);
    });
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [jobName]);

  const canConfirm = confirmText === 'VERSTUUR';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 32, maxWidth: 500, width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Kop */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#dc2626' }}>
            {t('cronDashboard.liveModalTitel')}
          </h2>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={20} />
          </button>
        </div>

        {/* Job naam */}
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6,
          padding: '10px 14px', marginBottom: 16, fontSize: 14,
        }}>
          <strong>{JOB_LABELS[jobName] || jobName}</strong>
        </div>

        {/* Waarschuwing */}
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
          {t('cronDashboard.liveModalWaarschuwing')}
        </p>

        {/* Actuele telling */}
        <div style={{
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '10px 14px', marginBottom: 20, fontSize: 14,
        }}>
          {loadingCount ? (
            <span style={{ color: '#6b7280' }}>{t('cronDashboard.previewLadenVoorLive')}</span>
          ) : previewCount === null ? (
            <span style={{ color: '#6b7280' }}>—</span>
          ) : previewCount === 0 ? (
            <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {t('cronDashboard.liveModalGeenClients')}</span>
          ) : (
            <span style={{ color: '#dc2626', fontWeight: 700 }}>
              {t('cronDashboard.liveModalAantalPrefix')} <strong>{previewCount}</strong> {t('cronDashboard.liveModalAantalSuffix')}
            </span>
          )}
        </div>

        {/* Invoerveld */}
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
          {t('cronDashboard.liveModalTypeerLabel')}
        </label>
        <input
          ref={inputRef}
          type="text"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value.toUpperCase())}
          placeholder={t('cronDashboard.liveModalTypeerPlaceholder')}
          style={{
            width: '100%', border: '2px solid ' + (canConfirm ? '#16a34a' : '#d1d5db'),
            borderRadius: 6, padding: '8px 12px', fontSize: 15, fontFamily: 'monospace',
            letterSpacing: 2, outline: 'none', boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
        />

        {/* Knoppen */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '10px 0', border: '1px solid #d1d5db', borderRadius: 6,
              background: '#fff', cursor: 'pointer', fontSize: 14, color: '#374151',
            }}
          >
            {t('cronDashboard.liveModalAnnuleren')}
          </button>
          <button
            onClick={() => canConfirm && onConfirm()}
            disabled={!canConfirm}
            style={{
              flex: 2, padding: '10px 0', border: 'none', borderRadius: 6,
              background: canConfirm ? '#dc2626' : '#fca5a5',
              color: '#fff', cursor: canConfirm ? 'pointer' : 'not-allowed',
              fontSize: 14, fontWeight: 700, transition: 'background 0.2s',
            }}
          >
            {t('cronDashboard.liveModalBevestigKnop')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Hoofd pagina
// ─────────────────────────────────────────────────────────────────
export default function CronDashboardPage() {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [myFullName, setMyFullName] = useState('');

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Planning
  const [jobs, setJobs]         = useState([]);

  // Historie
  const [runHistory, setRunHistory]     = useState([]);
  const [messageLog, setMessageLog]     = useState([]);
  const [selectedJob, setSelectedJob]   = useState(null);

  // Runstatus (cron_run_log)
  const [runLatest, setRunLatest]       = useState([]);
  const [runRecent, setRunRecent]       = useState([]);

  // Preview
  const [previewInvoice,             setPreviewInvoice]             = useState([]);
  const [previewProspect,            setPreviewProspect]            = useState([]);
  const [previewAgreement,           setPreviewAgreement]           = useState([]);
  const [previewRenewals,            setPreviewRenewals]            = useState(null);
  const [previewMembershipReminders, setPreviewMembershipReminders] = useState(null);
  const [previewLoading,             setPreviewLoading]             = useState(false);

  // Handmatige trigger
  const [triggerBusy, setTriggerBusy]           = useState({});  // jobName → boolean
  const [testAddresses, setTestAddresses]        = useState({}); // jobName → string
  const [liveModal, setLiveModal]               = useState(null); // jobName of null

  // Actief tab
  const [activeTab, setActiveTab] = useState('planning');

  useEffect(() => { loadAll(); }, []);

  // Initieer testadres met e-mailadres van de ingelogde coach + laad full_name
  useEffect(() => {
    if (!user) return;
    const email = user.email || '';
    const initial = {};
    Object.keys(JOB_FUNCTION).forEach(j => { initial[j] = email; });
    setTestAddresses(initial);
    // Haal full_name op voor triggered_by_name logging
    supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle()
      .then(({ data }) => { if (data?.full_name) setMyFullName(data.full_name); });
  }, [user]);

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      const [jobsRes, histRes, msgRes, latestRes, recentRes] = await Promise.all([
        supabase.rpc('get_cron_jobs'),
        supabase.rpc('get_cron_run_history', { p_limit: 30 }),
        supabase.rpc('get_cron_message_log',  { p_limit: 50 }),
        supabase.rpc('get_cron_run_log_latest'),
        supabase.rpc('get_cron_run_log_recent', { p_limit: 50 }),
      ]);
      if (jobsRes.error) throw new Error(jobsRes.error.message);
      if (histRes.error) throw new Error(histRes.error.message);
      if (msgRes.error)  throw new Error(msgRes.error.message);
      if (latestRes.error) throw new Error(latestRes.error.message);
      if (recentRes.error) throw new Error(recentRes.error.message);

      setJobs(jobsRes.data || []);
      setRunHistory(histRes.data || []);
      setMessageLog(msgRes.data || []);
      setRunLatest(latestRes.data || []);
      setRunRecent(recentRes.data || []);
    } catch (err) {
      setError(t('cronDashboard.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadPreview() {
    setPreviewLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const [invRes, proRes, agrRes, renewResponse, memRemResponse] = await Promise.all([
        supabase.rpc('preview_invoice_reminders'),
        supabase.rpc('preview_prospect_reminders'),
        supabase.rpc('preview_agreement_reminders'),
        fetch(`${supabaseUrl}/functions/v1/process-membership-renewals`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ test_mode: true }),
        }),
        fetch(`${supabaseUrl}/functions/v1/process-membership-reminders`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ test_mode: true }),
        }),
      ]);
      if (invRes.error) throw new Error(invRes.error.message);
      if (proRes.error) throw new Error(proRes.error.message);
      if (agrRes.error) throw new Error(agrRes.error.message);

      setPreviewInvoice(invRes.data   || []);
      setPreviewProspect(proRes.data  || []);
      setPreviewAgreement(agrRes.data || []);
      setPreviewRenewals(renewResponse.ok ? await renewResponse.json() : null);
      setPreviewMembershipReminders(memRemResponse.ok ? await memRemResponse.json() : null);
    } catch (err) {
      setError(t('cronDashboard.foutPreview') + err.message);
    } finally {
      setPreviewLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'preview') loadPreview();
  }, [activeTab]);

  // ─── Handmatige trigger ───────────────────────────────────────
  async function triggerJob(jobName, isTest) {
    const fnName = JOB_FUNCTION[jobName];
    if (!fnName) return;
    const testAddress = testAddresses[jobName] || '';

    if (isTest && !testAddress.includes('@')) {
      showError(t('cronDashboard.foutTrigger') + 'Vul een geldig testadres in.');
      return;
    }

    setTriggerBusy(prev => ({ ...prev, [jobName]: true }));
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/${fnName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(isTest
          ? { test_mode: true, test_address: testAddress, triggered_by_name: myFullName || '' }
          : { test_mode: false, triggered_by_name: myFullName || '' }
        ),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      // Bouw toast-bericht (per job-type)
      const sent = result.reminders?.sent ?? result.sent ?? 0;
      const eligible = result.reminders?.eligible ?? result.processed ?? result.sent ?? 0;
      const finalSent = result.finalNotices?.sent ?? 0;
      const errors = result.reminders?.errors ?? result.errors ?? 0;
      const skipped = result.reminders?.skipped ?? result.skipped ?? 0;
      const totalSent = sent + finalSent;

      // Job-specifieke suffix
      let suffix = '';
      if (jobName === 'daily-invoice-reminders') {
        suffix = 'herinneringen/aanmaningen';
      } else if (jobName === 'send-prospect-reminders-daily') {
        suffix = 'prospect herinneringen';
      } else if (jobName === 'send-agreement-reminders') {
        suffix = 'overeenkomst herinneringen';
      }

      if (isTest) {
        // Toon altijd het aantal VERZONDEN, niet eligible/processed
        const detailParts = [];
        if (errors > 0) detailParts.push(`${errors} gefaald`);
        if (skipped > 0) detailParts.push(`${skipped} overgeslagen`);
        const detail = detailParts.length > 0 ? ` (${detailParts.join(', ')})` : '';
        showSuccess(
          `Testrun geslaagd: ${totalSent} ${suffix} als testmail verzonden${detail}`
        );
      } else {
        showSuccess(
          `${t('cronDashboard.liveResultaatTitel')}: ${totalSent} ${suffix}`
        );
        // Vernieuw de historie na live run
        loadAll();
      }
    } catch (err) {
      showError(t('cronDashboard.foutTrigger') + err.message);
    } finally {
      setTriggerBusy(prev => ({ ...prev, [jobName]: false }));
    }
  }

  function handleTestKlik(jobName) {
    triggerJob(jobName, true);
  }

  function handleLiveKlik(jobName) {
    setLiveModal(jobName);
  }

  function handleLiveBevestigd() {
    const jobName = liveModal;
    setLiveModal(null);
    triggerJob(jobName, false);
  }

  // Uitvoeringshistorie gefilterd
  const filteredHistory = selectedJob
    ? runHistory.filter(r => r.jobname === selectedJob)
    : runHistory;

  const TAB_STYLE = (tab) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 20px',
    border: 'none',
    borderBottom: activeTab === tab ? '3px solid #1d9e75' : '3px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? '#1d9e75' : '#6b7280',
    fontSize: 15,
  });

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        <div className="spinner" />
        <p>{t('cronDashboard.laden')}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
        {t('cronDashboard.titel')}
      </h1>
      <p style={{ color: '#6b7280', margin: '0 0 24px', fontSize: 14 }}>
        {t('cronDashboard.subtitel')}
      </p>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 24, color: '#991b1b', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Live bevestigings-modal */}
      {liveModal && (
        <LiveConfirmModal
          jobName={liveModal}
          onConfirm={handleLiveBevestigd}
          onCancel={() => setLiveModal(null)}
        />
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 28 }}>
        <button style={TAB_STYLE('planning')} onClick={() => setActiveTab('planning')}>
          <CalendarClock size={16} />
          {t('cronDashboard.tabPlanning')}
        </button>
        <button style={TAB_STYLE('runstatus')} onClick={() => setActiveTab('runstatus')}>
          <Activity size={16} />
          {t('cronDashboard.tabRunstatus')}
        </button>
        <button style={TAB_STYLE('historie')} onClick={() => setActiveTab('historie')}>
          <History size={16} />
          {t('cronDashboard.tabHistorie')}
        </button>
        <button style={TAB_STYLE('preview')} onClick={() => setActiveTab('preview')}>
          <Eye size={16} />
          {t('cronDashboard.tabPreview')}
        </button>
      </div>

      {/* ══════════════ TAB: PLANNING ══════════════ */}
      {activeTab === 'planning' && (
        <Section title={t('cronDashboard.planningTitel')}>

          {/* Job-kaarten met knoppen */}
          {jobs.map(job => {
            const nextRun = nextRunFromCronExpr(job.schedule);
            const busy = triggerBusy[job.jobname];
            const testAddr = testAddresses[job.jobname] || '';

            return (
              <Card key={job.jobid} style={{ marginBottom: 20 }}>
                {/* Job header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: '#1f2937' }}>
                        {JOB_LABELS[job.jobname] || job.jobname}
                      </span>
                      {job.active
                        ? <Badge color="#16a34a" label={t('cronDashboard.actief')} />
                        : <Badge color="#6b7280" label={t('cronDashboard.inactief')} />
                      }
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280' }}>
                      {JOB_DESC[job.jobname] || '—'}
                    </p>
                    <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#9ca3af' }}>
                      {cronToHuman(job.schedule)}
                      {nextRun && (
                        <> &bull; {t('cronDashboard.colVolgende')}: {fmtDateTime(nextRun.toISOString())}</>
                      )}
                    </span>
                  </div>

                  {/* Knoppen sectie */}
                  {JOB_FUNCTION[job.jobname] && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 320 }}>

                      {/* Testadres invoer */}
                      <div>
                        <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                          {t('cronDashboard.testEmailLabel')}
                        </label>
                        <input
                          type="email"
                          value={testAddr}
                          onChange={e => setTestAddresses(prev => ({ ...prev, [job.jobname]: e.target.value }))}
                          placeholder={t('cronDashboard.testEmailPlaceholder')}
                          disabled={busy}
                          style={{
                            width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
                            padding: '6px 10px', fontSize: 13, boxSizing: 'border-box',
                            opacity: busy ? 0.6 : 1,
                          }}
                        />
                      </div>

                      {/* Twee knoppen naast elkaar */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {/* Test draaien */}
                        <button
                          onClick={() => handleTestKlik(job.jobname)}
                          disabled={busy}
                          title="Voert een testrun uit: e-mails gaan UITSLUITEND naar het testadres. Geen logging, geen statuswijziging."
                          style={{
                            flex: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                            background: busy ? '#f3f4f6' : '#f9fafb',
                            color: busy ? '#9ca3af' : '#374151',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                          }}
                        >
                          <Play size={14} />
                          {busy ? t('cronDashboard.bezig') : t('cronDashboard.btnTestDraaien')}
                        </button>

                        {/* Live draaien */}
                        <button
                          onClick={() => handleLiveKlik(job.jobname)}
                          disabled={busy}
                          title="Voert een ECHTE run uit: berichten gaan naar echte cliënten. Vereist bevestiging."
                          style={{
                            flex: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '8px 12px', border: '1px solid #fca5a5', borderRadius: 6,
                            background: busy ? '#f3f4f6' : '#fef2f2',
                            color: busy ? '#9ca3af' : '#dc2626',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                          }}
                        >
                          <Zap size={14} />
                          {busy ? t('cronDashboard.bezig') : t('cronDashboard.btnLiveDraaien')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}

          {jobs.length === 0 && (
            <Card>
              <p style={{ color: '#6b7280', margin: 0 }}>{t('cronDashboard.geenJobs')}</p>
            </Card>
          )}

          <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 8 }}>
            {t('cronDashboard.planningVoetnoot')}
          </p>
        </Section>
      )}

      {/* ══════════════ TAB: RUNSTATUS (cron_run_log) ══════════════ */}
      {activeTab === 'runstatus' && (() => {
        const latestMap = {};
        (runLatest || []).forEach(r => { latestMap[r.job_name] = r; });
        return (
          <>
            <Section title={t('cronDashboard.runstatusTitel')}>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
                {t('cronDashboard.runstatusUitleg')}
              </p>
              <Card>
                <TableWrapper>
                  <thead>
                    <tr>
                      <TH>{t('cronDashboard.colJob')}</TH>
                      <TH>{t('cronDashboard.colStatus')}</TH>
                      <TH>{t('cronDashboard.colLaatsteRun')}</TH>
                      <TH>{t('cronDashboard.colDuur')}</TH>
                      <TH>{t('cronDashboard.colSamenvatting')}</TH>
                      <TH>{t('cronDashboard.colFouten')}</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {RUNLOG_JOBS.map(job => {
                      const run = latestMap[job];
                      const st = run ? (RUN_STATUS[run.status] || { color: '#6b7280', label: run.status }) : null;
                      const duurMs = run && run.finished_at && run.started_at
                        ? new Date(run.finished_at) - new Date(run.started_at) : null;
                      const errCount = run && Array.isArray(run.errors) ? run.errors.length : 0;
                      return (
                        <tr key={job}>
                          <TD><span style={{ fontSize: 13, fontWeight: 600 }}>{RUNLOG_LABELS[job] || job}</span></TD>
                          <TD>
                            {st
                              ? <Badge color={st.color} label={st.label} />
                              : <Badge color="#6b7280" label={t('cronDashboard.nooitGedraaid')} />}
                          </TD>
                          <TD style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                            {run ? fmtDateTime(run.started_at) : '—'}
                          </TD>
                          <TD style={{ fontSize: 12, color: '#6b7280' }}>
                            {duurMs != null ? `${(duurMs / 1000).toFixed(1)}s` : '—'}
                          </TD>
                          <TD style={{ fontSize: 12, color: '#4b5563', maxWidth: 340, wordBreak: 'break-word' }}>
                            {run ? fmtSummary(run.summary) : '—'}
                          </TD>
                          <TD style={{ fontSize: 12 }}>
                            {errCount > 0
                              ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{errCount}</span>
                              : <span style={{ color: '#16a34a' }}>{t('cronDashboard.geenFouten')}</span>}
                          </TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </TableWrapper>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12, marginBottom: 0 }}>
                  {t('cronDashboard.runstatusVoetnoot')}
                </p>
              </Card>
            </Section>

            <Section title={t('cronDashboard.runstatusRecenteTitel')}>
              <Card>
                {(!runRecent || runRecent.length === 0) ? (
                  <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>{t('cronDashboard.geenRunstatus')}</p>
                ) : (
                  <TableWrapper>
                    <thead>
                      <tr>
                        <TH>{t('cronDashboard.colStarttijd')}</TH>
                        <TH>{t('cronDashboard.colJob')}</TH>
                        <TH>{t('cronDashboard.colStatus')}</TH>
                        <TH>{t('cronDashboard.colDuur')}</TH>
                        <TH>{t('cronDashboard.colSamenvatting')}</TH>
                        <TH>{t('cronDashboard.colFouten')}</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {runRecent.map(run => {
                        const st = RUN_STATUS[run.status] || { color: '#6b7280', label: run.status };
                        const duurMs = run.finished_at && run.started_at
                          ? new Date(run.finished_at) - new Date(run.started_at) : null;
                        const errs = Array.isArray(run.errors) ? run.errors : [];
                        return (
                          <tr key={run.id}>
                            <TD style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                              {fmtDateTime(run.started_at)}
                            </TD>
                            <TD style={{ fontSize: 12 }}>{RUNLOG_LABELS[run.job_name] || run.job_name}</TD>
                            <TD><Badge color={st.color} label={st.label} /></TD>
                            <TD style={{ fontSize: 12, color: '#6b7280' }}>
                              {duurMs != null ? `${(duurMs / 1000).toFixed(1)}s` : '—'}
                            </TD>
                            <TD style={{ fontSize: 12, color: '#4b5563', maxWidth: 300, wordBreak: 'break-word' }}>
                              {fmtSummary(run.summary)}
                            </TD>
                            <TD style={{ fontSize: 12, color: '#991b1b', maxWidth: 280, wordBreak: 'break-word' }}>
                              {errs.length === 0
                                ? <span style={{ color: '#16a34a' }}>{t('cronDashboard.geenFouten')}</span>
                                : errs.slice(0, 3).map((e, i) => (
                                    <div key={i}>{typeof e === 'object' ? (e.message || e.detail || JSON.stringify(e)) : String(e)}</div>
                                  ))}
                              {errs.length > 3 && <div>… +{errs.length - 3}</div>}
                            </TD>
                          </tr>
                        );
                      })}
                    </tbody>
                  </TableWrapper>
                )}
              </Card>
            </Section>
          </>
        );
      })()}

      {/* ══════════════ TAB: HISTORIE ══════════════ */}
      {activeTab === 'historie' && (
        <>
          <Section title={t('cronDashboard.historieTitelUitvoering')}>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#92400e', fontSize: 13 }}>
              {t('cronDashboard.historieMisleidendNote')}
            </div>
            <Card>
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontSize: 13, color: '#4b5563', fontWeight: 600 }}>
                  {t('cronDashboard.filterJob')}
                </label>
                <select
                  value={selectedJob || ''}
                  onChange={e => setSelectedJob(e.target.value || null)}
                  style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', fontSize: 13 }}
                >
                  <option value="">{t('cronDashboard.alleJobs')}</option>
                  {jobs.map(j => (
                    <option key={j.jobid} value={j.jobname}>
                      {JOB_LABELS[j.jobname] || j.jobname}
                    </option>
                  ))}
                </select>
              </div>

              {runHistory.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 14 }}>{t('cronDashboard.geenUitvoeringsdata')}</p>
              ) : (
                <TableWrapper>
                  <thead>
                    <tr>
                      <TH>{t('cronDashboard.colStarttijd')}</TH>
                      <TH>{t('cronDashboard.colJob')}</TH>
                      <TH>{t('cronDashboard.colStatus')}</TH>
                      <TH>{t('cronDashboard.colDuur')}</TH>
                      <TH>{t('cronDashboard.colResultaat')}</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((run, i) => {
                      const duurMs = run.end_time && run.start_time
                        ? new Date(run.end_time) - new Date(run.start_time)
                        : null;
                      const duurSec = duurMs != null ? (duurMs / 1000).toFixed(1) : null;
                      const statusColor = STATUS_COLORS[run.status] || '#6b7280';
                      return (
                        <tr key={i}>
                          <TD style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                            {fmtDateTime(run.start_time)}
                          </TD>
                          <TD>
                            <span style={{ fontSize: 12 }}>{JOB_LABELS[run.jobname] || run.jobname}</span>
                          </TD>
                          <TD>
                            <Badge color={statusColor} label={run.status} />
                          </TD>
                          <TD style={{ fontSize: 12, color: '#6b7280' }}>
                            {duurSec != null ? `${duurSec}s` : '—'}
                          </TD>
                          <TD style={{ fontSize: 12, color: '#4b5563', maxWidth: 320, wordBreak: 'break-word' }}>
                            {run.return_message || '—'}
                          </TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </TableWrapper>
              )}
            </Card>
          </Section>

          <Section title={t('cronDashboard.historieTitelBerichten')}>
            <Card>
              {messageLog.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 14 }}>{t('cronDashboard.geenBerichten')}</p>
              ) : (
                <TableWrapper>
                  <thead>
                    <tr>
                      <TH>{t('cronDashboard.colDatum')}</TH>
                      <TH>{t('cronDashboard.colJob')}</TH>
                      <TH>{t('cronDashboard.colClient')}</TH>
                      <TH>{t('cronDashboard.colType')}</TH>
                      <TH>{t('cronDashboard.colDetails')}</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {messageLog.map((msg, i) => (
                      <tr key={i}>
                        <TD style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {fmtDateTime(msg.sent_at)}
                        </TD>
                        <TD style={{ fontSize: 12 }}>
                          {JOB_LABELS[msg.job_name] || msg.job_name}
                        </TD>
                        <TD style={{ fontWeight: 600 }}>
                          {msg.client_name || '—'}
                        </TD>
                        <TD>
                          <Badge
                            color={
                              msg.message_type === 'reminder'          ? '#d97706' :
                              msg.message_type === 'final_notice'      ? '#dc2626' :
                              msg.message_type === 'prospect_reminder' ? '#7c3aed' :
                                                                         '#2563eb'
                            }
                            label={TYPE_LABELS[msg.message_type] || msg.message_type}
                          />
                        </TD>
                        <TD style={{ fontSize: 12, color: '#4b5563' }}>
                          {msg.details || '—'}
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </TableWrapper>
              )}
            </Card>
          </Section>
        </>
      )}

      {/* ══════════════ TAB: PREVIEW ══════════════ */}
      {activeTab === 'preview' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
              {t('cronDashboard.previewUitleg')}
            </p>
            <button
              onClick={loadPreview}
              disabled={previewLoading}
              style={{
                background: '#1d9e75', color: '#fff', border: 'none', borderRadius: 6,
                padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                opacity: previewLoading ? 0.7 : 1,
              }}
            >
              {previewLoading ? t('cronDashboard.previewLaden') : t('cronDashboard.previewVernieuwen')}
            </button>
          </div>

          {previewLoading && (
            <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>
              <div className="spinner" />
            </div>
          )}

          {!previewLoading && (
            <>
              {/* Factuurherinneringen preview */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                    {JOB_LABELS['daily-invoice-reminders']}
                  </h3>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('cronDashboard.volgendeUitvoering')}: <strong>08:00 UTC</strong>
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  {JOB_DESC['daily-invoice-reminders']}
                </p>

                {previewInvoice.length === 0 ? (
                  <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
                    ✓ {t('cronDashboard.geenVerzendingen')}
                  </p>
                ) : (
                  <>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#d97706', marginBottom: 8 }}>
                      {previewInvoice.length + ' ' + t('cronDashboard.berichtenBijVolgendeRun')}
                    </p>
                    <TableWrapper>
                      <thead>
                        <tr>
                          <TH>{t('cronDashboard.colClient')}</TH>
                          <TH>{t('cronDashboard.colFactuurnummer')}</TH>
                          <TH>{t('cronDashboard.colType')}</TH>
                          <TH>{t('cronDashboard.colBetaaldatum')}</TH>
                          <TH>{t('cronDashboard.colDagenVervallen')}</TH>
                          <TH>{t('cronDashboard.colBedrag')}</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {previewInvoice.map((row, i) => (
                          <tr key={i}>
                            <TD style={{ fontWeight: 600 }}>{row.client_name}</TD>
                            <TD style={{ fontFamily: 'monospace', fontSize: 13 }}>{row.invoice_number}</TD>
                            <TD>
                              <Badge
                                color={row.reminder_type === 'final_notice' ? '#dc2626' : '#d97706'}
                                label={TYPE_LABELS[row.reminder_type] || row.reminder_type}
                              />
                            </TD>
                            <TD style={{ fontSize: 12 }}>{fmtDate(row.due_date)}</TD>
                            <TD style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                              {row.days_overdue} {t('cronDashboard.dagen')}
                            </TD>
                            <TD style={{ fontFamily: 'monospace', fontSize: 13 }}>
                              {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(row.total_amount)}
                            </TD>
                          </tr>
                        ))}
                      </tbody>
                    </TableWrapper>
                  </>
                )}
              </Card>

              {/* Prospect herinneringen preview */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                    {JOB_LABELS['send-prospect-reminders-daily']}
                  </h3>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('cronDashboard.volgendeUitvoering')}: <strong>10:00 UTC</strong>
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  {JOB_DESC['send-prospect-reminders-daily']}
                </p>

                {previewProspect.length === 0 ? (
                  <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
                    ✓ {t('cronDashboard.geenVerzendingen')}
                  </p>
                ) : (
                  <>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed', marginBottom: 8 }}>
                      {previewProspect.length + ' ' + t('cronDashboard.berichtenBijVolgendeRun')}
                    </p>
                    <TableWrapper>
                      <thead>
                        <tr>
                          <TH>{t('cronDashboard.colClient')}</TH>
                          <TH>{t('cronDashboard.colProefsessie')}</TH>
                          <TH>{t('cronDashboard.colAantalHerinneringen')}</TH>
                          <TH>{t('cronDashboard.colLaatsteHerinnering')}</TH>
                          <TH>{t('cronDashboard.colInterval')}</TH>
                          <TH>{t('cronDashboard.colMax')}</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {previewProspect.map((row, i) => (
                          <tr key={i}>
                            <TD style={{ fontWeight: 600 }}>{row.client_name}</TD>
                            <TD style={{ fontSize: 12 }}>{fmtDate(row.trial_date)}</TD>
                            <TD style={{ fontSize: 13 }}>{row.reminder_count}</TD>
                            <TD style={{ fontSize: 12 }}>{fmtDateTime(row.last_reminder_sent) || '—'}</TD>
                            <TD style={{ fontSize: 12 }}>{row.interval_days} {t('cronDashboard.dagAfkorting')}</TD>
                            <TD style={{ fontSize: 12 }}>{row.max_reminders}</TD>
                          </tr>
                        ))}
                      </tbody>
                    </TableWrapper>
                  </>
                )}
              </Card>

              {/* Overeenkomst herinneringen preview */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                    {JOB_LABELS['send-agreement-reminders']}
                  </h3>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('cronDashboard.volgendeUitvoering')}: <strong>09:00 UTC</strong>
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  {JOB_DESC['send-agreement-reminders']}
                </p>

                {previewAgreement.length === 0 ? (
                  <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
                    ✓ {t('cronDashboard.geenVerzendingen')}
                  </p>
                ) : (
                  <>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', marginBottom: 8 }}>
                      {previewAgreement.length + ' ' + t('cronDashboard.berichtenBijVolgendeRun')}
                    </p>
                    <TableWrapper>
                      <thead>
                        <tr>
                          <TH>{t('cronDashboard.colClient')}</TH>
                          <TH>{t('cronDashboard.colVerlooptOp')}</TH>
                          <TH>{t('cronDashboard.colAantalHerinneringen')}</TH>
                          <TH>{t('cronDashboard.colLaatsteHerinnering')}</TH>
                          <TH>{t('cronDashboard.colInterval')}</TH>
                          <TH>{t('cronDashboard.colMax')}</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {previewAgreement.map((row, i) => (
                          <tr key={i}>
                            <TD style={{ fontWeight: 600 }}>{row.client_name}</TD>
                            <TD style={{ fontSize: 12 }}>{fmtDateTime(row.expires_at)}</TD>
                            <TD style={{ fontSize: 13 }}>{row.reminder_count}</TD>
                            <TD style={{ fontSize: 12 }}>{fmtDateTime(row.last_reminder_sent) || '—'}</TD>
                            <TD style={{ fontSize: 12 }}>{row.interval_days} {t('cronDashboard.dagAfkorting')}</TD>
                            <TD style={{ fontSize: 12 }}>{row.max_reminders}</TD>
                          </tr>
                        ))}
                      </tbody>
                    </TableWrapper>
                  </>
                )}
              </Card>

              {/* Abonnementsbeheer preview (dry run via test_mode=true) */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                    {JOB_LABELS['process-membership-renewals']}
                  </h3>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('cronDashboard.volgendeUitvoering')}: <strong>00:00 UTC</strong>
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  {JOB_DESC['process-membership-renewals']}
                </p>

                {!previewRenewals ? (
                  <p style={{ color: '#6b7280', fontSize: 14 }}>—</p>
                ) : (previewRenewals.cancellations + previewRenewals.credits_booked + previewRenewals.prolongations + previewRenewals.invoices_created) === 0 ? (
                  <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
                    ✓ {t('cronDashboard.previewRenewalsGeenActies')}
                  </p>
                ) : (
                  <TableWrapper>
                    <thead>
                      <tr>
                        <TH>{t('cronDashboard.colType')}</TH>
                        <TH>{t('cronDashboard.colAantalHerinneringen')}</TH>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <TD>{t('cronDashboard.previewRenewalsCancellations')}</TD>
                        <TD style={{ fontWeight: 600 }}>{previewRenewals.cancellations}</TD>
                      </tr>
                      <tr>
                        <TD>{t('cronDashboard.previewRenewalsCredits')}</TD>
                        <TD style={{ fontWeight: 600 }}>{previewRenewals.credits_booked}</TD>
                      </tr>
                      <tr>
                        <TD>{t('cronDashboard.previewRenewalsProlongations')}</TD>
                        <TD style={{ fontWeight: 600 }}>{previewRenewals.prolongations}</TD>
                      </tr>
                      <tr>
                        <TD>{t('cronDashboard.previewRenewalsInvoices')}</TD>
                        <TD style={{ fontWeight: 600 }}>{previewRenewals.invoices_created}</TD>
                      </tr>
                    </tbody>
                  </TableWrapper>
                )}
              </Card>

              {/* Abonnementsverloopherinneringen preview (dry run via test_mode=true) */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                    {JOB_LABELS['process-membership-reminders']}
                  </h3>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('cronDashboard.volgendeUitvoering')}: <strong>06:00 UTC</strong>
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  {JOB_DESC['process-membership-reminders']}
                </p>

                {!previewMembershipReminders ? (
                  <p style={{ color: '#6b7280', fontSize: 14 }}>—</p>
                ) : (previewMembershipReminders.prolongation_reminders_sent + previewMembershipReminders.cancellation_reminders_sent) === 0 ? (
                  <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
                    ✓ {t('cronDashboard.previewMembershipGeenHerinneringen')}
                  </p>
                ) : (
                  <TableWrapper>
                    <thead>
                      <tr>
                        <TH>{t('cronDashboard.colType')}</TH>
                        <TH>{t('cronDashboard.colAantalHerinneringen')}</TH>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <TD>{t('cronDashboard.previewMembershipProlongReminders')}</TD>
                        <TD style={{ fontWeight: 600 }}>{previewMembershipReminders.prolongation_reminders_sent}</TD>
                      </tr>
                      <tr>
                        <TD>{t('cronDashboard.previewMembershipCancReminders')}</TD>
                        <TD style={{ fontWeight: 600 }}>{previewMembershipReminders.cancellation_reminders_sent}</TD>
                      </tr>
                    </tbody>
                  </TableWrapper>
                )}
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
